import React from 'react';
import { Activity, AlertTriangle, BarChart3, CalendarCheck, ChevronLeft, ChevronRight, ClipboardList, Clock, Eye, Flame, PhoneCall, Target, TrendingUp, UserCircle, X, Zap } from 'lucide-react';
import { Button } from '../../ui/button';
import { timeAgo } from '../../../utils/admin-helpers';
import { useAdminCtx } from '../AdminDashboardContext';
import { ConsentBadge } from '../ConsentBadge';

export default function AssessmentsTab() {
  const { assessmentDetail, assessmentIndustryFilter, assessmentNextActionFilter, assessmentSortBy, assessmentTagFilter, assessmentTeamSizeFilter, assessments, assessmentsPage, assessmentsPages, assessmentsStats, assessmentsTotal, fetchAssessmentDetail, fetchAssessments, nextActionOptions, selectedAssessmentId, setAssessmentDetail, setAssessmentIndustryFilter, setAssessmentNextActionFilter, setAssessmentSortBy, setAssessmentTagFilter, setAssessmentTeamSizeFilter, setSelectedAssessmentId, topPriorityLeads, updateEngagement, updatePipeline } = useAdminCtx();
  return (
    <div data-testid="assessments-section">
      {/* Top Priority Leads Highlight */}
      {topPriorityLeads.length > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-violet-500/10 via-slate-900 to-cyan-500/10 border border-violet-500/20">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-5 w-5 text-violet-400" />
            <h3 className="text-white font-semibold">Top Priority Leads</h3>
            <span className="text-slate-500 text-sm">(Contact First)</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {topPriorityLeads.map((lead, i) => {
              const tagColor = lead.lead_tag === 'Urgent' ? 'border-red-500/50 bg-red-500/10' : lead.lead_tag === 'Hot' ? 'border-amber-500/50 bg-amber-500/10' : 'border-emerald-500/50 bg-emerald-500/10';
              return (
                <button
                  key={lead.id}
                  onClick={() => fetchAssessmentDetail(lead.id)}
                  className={`p-3 rounded-lg border ${tagColor} hover:bg-slate-800/50 transition-all text-left`}
                  data-testid={`priority-lead-${i}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-400">#{i + 1}</span>
                    <span className="text-xs font-bold text-violet-400">{lead.priority_score} pts</span>
                  </div>
                  <p className="text-white font-medium text-sm truncate">{lead.name}</p>
                  <p className="text-slate-500 text-xs truncate">{lead.company}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-xs font-bold ${lead.lead_tag === 'Urgent' ? 'text-red-400' : lead.lead_tag === 'Hot' ? 'text-amber-400' : 'text-emerald-400'}`}>
                      {lead.lead_tag}
                    </span>
                    <span className="text-xs text-slate-400">{lead.alignment_score}%</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {assessmentsStats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <ClipboardList className="h-4 w-4 text-violet-400" />
              <span className="text-slate-400 text-xs font-medium">Total</span>
            </div>
            <p className="text-2xl font-bold text-white">{assessmentsStats.total}</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-900 border border-red-500/30">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-400" />
              <span className="text-slate-400 text-xs font-medium">Urgent</span>
            </div>
            <p className="text-2xl font-bold text-red-400">{assessmentsStats.urgent}</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-900 border border-amber-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="h-4 w-4 text-amber-400" />
              <span className="text-slate-400 text-xs font-medium">Hot</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">{assessmentsStats.hot}</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-900 border border-emerald-500/30">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
              <span className="text-slate-400 text-xs font-medium">Warm</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">{assessmentsStats.warm}</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-cyan-400" />
              <span className="text-slate-400 text-xs font-medium">Avg Align</span>
            </div>
            <p className="text-2xl font-bold text-cyan-400">{assessmentsStats.avg_alignment || 0}%</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="h-4 w-4 text-amber-400" />
              <span className="text-slate-400 text-xs font-medium">Avg Engage</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">{assessmentsStats.avg_engagement || 0}</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-violet-400" />
              <span className="text-slate-400 text-xs font-medium">Avg Priority</span>
            </div>
            <p className="text-2xl font-bold text-violet-400">{assessmentsStats.avg_priority || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select 
          value={assessmentNextActionFilter} 
          onChange={(e) => setAssessmentNextActionFilter(e.target.value)}
          data-testid="assessment-action-filter"
          className="h-10 px-3 rounded-md bg-slate-900 border border-violet-500/50 text-white text-sm focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Actions</option>
          <option value="New">🆕 New</option>
          <option value="Call">📞 Call</option>
          <option value="Email">📧 Email</option>
          <option value="Follow-up">🔄 Follow-up</option>
          <option value="Waiting">⏳ Waiting</option>
          <option value="Booked">📅 Booked</option>
          <option value="Closed Won">✅ Closed Won</option>
          <option value="Closed Lost">❌ Closed Lost</option>
        </select>
        <select 
          value={assessmentTagFilter} 
          onChange={(e) => setAssessmentTagFilter(e.target.value)}
          data-testid="assessment-tag-filter"
          className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Lead Status</option>
          <option value="Urgent">🔴 Urgent</option>
          <option value="Hot">🟠 Hot</option>
          <option value="Warm">🟢 Warm</option>
        </select>
        <select 
          value={assessmentIndustryFilter} 
          onChange={(e) => setAssessmentIndustryFilter(e.target.value)}
          data-testid="assessment-industry-filter"
          className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Industries</option>
          <option value="Corporate / Enterprise">Corporate / Enterprise</option>
          <option value="Manufacturing">Manufacturing</option>
          <option value="Healthcare">Healthcare</option>
          <option value="Education">Education</option>
          <option value="Government">Government</option>
          <option value="Nonprofit">Nonprofit</option>
          <option value="Other">Other</option>
        </select>
        <select 
          value={assessmentTeamSizeFilter} 
          onChange={(e) => setAssessmentTeamSizeFilter(e.target.value)}
          data-testid="assessment-teamsize-filter"
          className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none"
        >
          <option value="">All Team Sizes</option>
          <option value="1-10">1-10</option>
          <option value="11-50">11-50</option>
          <option value="51-200">51-200</option>
          <option value="201-500">201-500</option>
          <option value="500+">500+</option>
        </select>
        <select 
          value={assessmentSortBy} 
          onChange={(e) => setAssessmentSortBy(e.target.value)}
          data-testid="assessment-sort"
          className="h-10 px-3 rounded-md bg-slate-900 border border-slate-700 text-white text-sm focus:border-cyan-500 focus:outline-none"
        >
          <option value="priority">🎯 Priority Score</option>
          <option value="engagement">⚡ Engagement</option>
          <option value="alignment_asc">📉 Lowest Alignment</option>
          <option value="alignment_desc">📈 Highest Alignment</option>
          <option value="next_action">📋 Next Action</option>
          <option value="last_contacted">📅 Last Contacted</option>
          <option value="newest">🕐 Newest</option>
          <option value="oldest">🕐 Oldest</option>
        </select>
      </div>

      {/* Pipeline Quick Stats */}
      {assessmentsStats && (
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="px-2 py-1 rounded text-xs bg-blue-500/15 text-blue-400 border border-blue-500/30">🆕 New: {assessmentsStats.action_new || 0}</span>
          <span className="px-2 py-1 rounded text-xs bg-amber-500/15 text-amber-400 border border-amber-500/30">📞 Call: {assessmentsStats.action_call || 0}</span>
          <span className="px-2 py-1 rounded text-xs bg-violet-500/15 text-violet-400 border border-violet-500/30">🔄 Follow-up: {assessmentsStats.action_followup || 0}</span>
          <span className="px-2 py-1 rounded text-xs bg-cyan-500/15 text-cyan-400 border border-cyan-500/30">📅 Booked: {assessmentsStats.action_booked || 0}</span>
          <span className="px-2 py-1 rounded text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">✅ Won: {assessmentsStats.action_won || 0}</span>
          <span className="px-2 py-1 rounded text-xs bg-slate-500/15 text-slate-400 border border-slate-500/30">❌ Lost: {assessmentsStats.action_lost || 0}</span>
        </div>
      )}

      <p className="text-slate-400 text-sm mb-3">{assessmentsTotal} assessment{assessmentsTotal !== 1 ? 's' : ''} found</p>

      {/* Enhanced Assessments Table */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="assessments-table">
            <thead>
              <tr className="border-b border-slate-800 text-left">
                <th className="px-3 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider">Name</th>
                <th className="px-3 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Company</th>
                <th className="px-3 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider text-center">Align</th>
                <th className="px-3 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider text-center">Lead</th>
                <th className="px-3 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider text-center hidden md:table-cell">Priority</th>
                <th className="px-3 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider">Next Action</th>
                <th className="px-3 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Last Contact</th>
                <th className="px-3 py-3 text-slate-400 text-xs font-semibold uppercase tracking-wider text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {assessments.length > 0 ? assessments.map((a) => {
                const tagColor = a.lead_tag === 'Urgent' 
                  ? 'bg-red-500/15 text-red-400 border-red-500/30' 
                  : a.lead_tag === 'Hot' 
                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
                    : 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
                const alignColor = a.alignment_score >= 80 
                  ? 'text-emerald-400' 
                  : a.alignment_score >= 50 
                    ? 'text-amber-400' 
                    : 'text-red-400';
                const priorityColor = a.priority_score >= 70 
                  ? 'text-violet-400 bg-violet-500/15' 
                  : a.priority_score >= 50 
                    ? 'text-cyan-400 bg-cyan-500/15' 
                    : 'text-slate-400 bg-slate-700/50';

                // Next Action styling
                const nextActionColor = {
                  'New': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
                  'Call': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
                  'Email': 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
                  'Follow-up': 'bg-violet-500/15 text-violet-400 border-violet-500/30',
                  'Waiting': 'bg-slate-500/15 text-slate-400 border-slate-500/30',
                  'Booked': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
                  'Closed Won': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
                  'Closed Lost': 'bg-red-500/15 text-red-400 border-red-500/30'
                }[a.next_action] || 'bg-slate-500/15 text-slate-400 border-slate-500/30';

                return (
                  <tr 
                    key={a.id} 
                    className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors"
                    data-testid={`assessment-row-${a.id}`}
                  >
                    <td className="px-4 py-3 cursor-pointer" onClick={() => fetchAssessmentDetail(a.id)}>
                      <div className="flex flex-col">
                        <span className="text-white font-medium text-sm">{a.lead_info?.first_name} {a.lead_info?.last_name}</span>
                        <span className="text-slate-500 text-xs truncate max-w-[150px]">{a.lead_info?.email}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell cursor-pointer" onClick={() => fetchAssessmentDetail(a.id)}>
                      <div className="flex flex-col">
                        <span className="text-slate-300 text-sm">{a.lead_info?.company_name}</span>
                        <span className="text-slate-500 text-xs">{a.lead_info?.job_title}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center cursor-pointer" onClick={() => fetchAssessmentDetail(a.id)}>
                      <span className={`text-lg font-bold ${alignColor}`}>{a.alignment_score}%</span>
                    </td>
                    <td className="px-4 py-3 text-center cursor-pointer" onClick={() => fetchAssessmentDetail(a.id)}>
                      <span className={`px-2 py-1 rounded-md text-xs font-bold border ${tagColor}`}>
                        {a.lead_tag}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell cursor-pointer" onClick={() => fetchAssessmentDetail(a.id)}>
                      <span className={`px-2 py-1 rounded-md text-sm font-bold ${priorityColor}`}>
                        {a.priority_score}
                      </span>
                    </td>
                    <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={a.next_action || 'New'}
                        onChange={(e) => updatePipeline(a.id, e.target.value, false)}
                        data-testid={`next-action-select-${a.id}`}
                        className={`px-2 py-1.5 rounded-md text-xs font-semibold border cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${nextActionColor}`}
                        style={{ backgroundColor: 'transparent' }}
                      >
                        {nextActionOptions.map(opt => (
                          <option key={opt} value={opt} className="bg-slate-900 text-white">{opt}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-xs hidden lg:table-cell">
                      {a.last_contacted ? (
                        <span className="text-emerald-400">{timeAgo(a.last_contacted)}</span>
                      ) : (
                        <span className="text-slate-500">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-center">
                        <Button
                          onClick={(e) => { e.stopPropagation(); updatePipeline(a.id, a.next_action || 'New', true); }}
                          size="sm"
                          variant="ghost"
                          className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 p-1.5"
                          title="Mark Contacted"
                          data-testid={`mark-contacted-${a.id}`}
                        >
                          <PhoneCall className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={(e) => { e.stopPropagation(); fetchAssessmentDetail(a.id); }}
                          size="sm"
                          variant="ghost"
                          className="text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 p-1.5"
                          title="View Details"
                          data-testid={`view-detail-${a.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-slate-500 text-sm">
                    No assessments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {assessmentsPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-800">
            <span className="text-slate-400 text-sm">Page {assessmentsPage} of {assessmentsPages}</span>
            <div className="flex gap-2">
              <Button
                onClick={() => fetchAssessments(assessmentsPage - 1)}
                disabled={assessmentsPage <= 1}
                variant="outline"
                size="sm"
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="assessments-prev-page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => fetchAssessments(assessmentsPage + 1)}
                disabled={assessmentsPage >= assessmentsPages}
                variant="outline"
                size="sm"
                className="border-slate-700 text-white hover:bg-slate-800"
                data-testid="assessments-next-page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Assessment Detail Modal */}
      {selectedAssessmentId && assessmentDetail && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setSelectedAssessmentId(null); setAssessmentDetail(null); }}>
          <div className="bg-slate-900 rounded-2xl border border-slate-700 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="assessment-detail-modal">
            {/* Header */}
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6 flex items-center justify-between z-10">
              <div>
                <h2 className="text-xl font-bold text-white">{assessmentDetail.lead_info?.first_name} {assessmentDetail.lead_info?.last_name}</h2>
                <p className="text-slate-400 text-sm">{assessmentDetail.lead_info?.company_name} • {assessmentDetail.lead_info?.job_title}</p>
              </div>
              <Button variant="ghost" onClick={() => { setSelectedAssessmentId(null); setAssessmentDetail(null); }} className="text-slate-400 hover:text-white" data-testid="close-modal-btn">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Sales Pipeline Actions - NEW */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-violet-500/10 border border-cyan-500/30">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-slate-400 text-xs uppercase mb-1">Next Action</p>
                      <select
                        value={assessmentDetail.next_action || 'New'}
                        onChange={(e) => updatePipeline(selectedAssessmentId, e.target.value, false)}
                        data-testid="modal-next-action-select"
                        className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-cyan-500/50 cursor-pointer"
                      >
                        {nextActionOptions.map(opt => (
                          <option key={opt} value={opt} className="bg-slate-900">{opt}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs uppercase mb-1">Last Contacted</p>
                      <p className="text-white font-medium" data-testid="modal-last-contacted">
                        {assessmentDetail.last_contacted ? (
                          <span className="text-emerald-400">{new Date(assessmentDetail.last_contacted).toLocaleString()}</span>
                        ) : (
                          <span className="text-slate-500">Never contacted</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => updatePipeline(selectedAssessmentId, assessmentDetail.next_action || 'New', true)}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white"
                      data-testid="modal-mark-contacted-btn"
                    >
                      <PhoneCall className="h-4 w-4 mr-2" /> Mark Contacted
                    </Button>
                    <Button
                      onClick={() => updatePipeline(selectedAssessmentId, 'Booked', true)}
                      className="bg-cyan-600 hover:bg-cyan-500 text-white"
                      data-testid="modal-mark-booked-btn"
                    >
                      <CalendarCheck className="h-4 w-4 mr-2" /> Mark Booked
                    </Button>
                  </div>
                </div>
              </div>

              {/* Scores Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 text-center">
                  <p className="text-slate-400 text-xs uppercase mb-1">Alignment</p>
                  <p className={`text-3xl font-bold ${assessmentDetail.score_data?.overall_score >= 80 ? 'text-emerald-400' : assessmentDetail.score_data?.overall_score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                    {assessmentDetail.score_data?.overall_score}%
                  </p>
                  <p className="text-xs text-slate-500">{assessmentDetail.alignment_level}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 text-center">
                  <p className="text-slate-400 text-xs uppercase mb-1">Lead Status</p>
                  <p className={`text-2xl font-bold ${assessmentDetail.lead_tag === 'Urgent' ? 'text-red-400' : assessmentDetail.lead_tag === 'Hot' ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {assessmentDetail.lead_tag}
                  </p>
                </div>
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 text-center">
                  <p className="text-slate-400 text-xs uppercase mb-1">Engagement</p>
                  <p className="text-3xl font-bold text-cyan-400">{assessmentDetail.engagement_score}</p>
                  <div className="w-full h-1.5 bg-slate-700 rounded-full mt-2">
                    <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${assessmentDetail.engagement_score}%` }}></div>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700 text-center">
                  <p className="text-slate-400 text-xs uppercase mb-1">Priority</p>
                  <p className="text-3xl font-bold text-violet-400">{assessmentDetail.priority_score}</p>
                </div>
              </div>

              {/* Contact Info & Engagement */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><UserCircle className="h-4 w-4 text-violet-400" /> Contact Info</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-400">Email:</span> <a href={`mailto:${assessmentDetail.lead_info?.email}`} className="text-cyan-400 hover:text-cyan-300">{assessmentDetail.lead_info?.email}</a> <ConsentBadge record={assessmentDetail} /></p>
                    <p><span className="text-slate-400">Industry:</span> <span className="text-white">{assessmentDetail.lead_info?.industry}</span></p>
                    <p><span className="text-slate-400">Team Size:</span> <span className="text-white">{assessmentDetail.lead_info?.team_size}</span></p>
                    <p><span className="text-slate-400">Submitted:</span> <span className="text-white">{new Date(assessmentDetail.created_at).toLocaleString()}</span></p>
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Zap className="h-4 w-4 text-amber-400" /> Engagement Activity</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-400">Assessment:</span> <span className="text-emerald-400">✓ Completed</span></p>
                    <p><span className="text-slate-400">Emails Opened:</span> <span className="text-white">{assessmentDetail.engagement?.email_opens || 0}</span></p>
                    <p><span className="text-slate-400">Links Clicked:</span> <span className="text-white">{assessmentDetail.engagement?.link_clicks || 0}</span></p>
                    <p><span className="text-slate-400">Consultation:</span> <span className={assessmentDetail.engagement?.consultation_booked ? 'text-emerald-400' : 'text-slate-500'}>{assessmentDetail.engagement?.consultation_booked ? '✓ Booked' : 'Not yet'}</span></p>
                  </div>
                  <div className="flex gap-2 mt-4 flex-wrap">
                    <Button size="sm" onClick={() => updateEngagement(selectedAssessmentId, 'email_open')} className="bg-slate-700 hover:bg-slate-600 text-white text-xs" data-testid="btn-email-open">
                      + Email Open
                    </Button>
                    <Button size="sm" onClick={() => updateEngagement(selectedAssessmentId, 'link_click')} className="bg-slate-700 hover:bg-slate-600 text-white text-xs" data-testid="btn-link-click">
                      + Link Click
                    </Button>
                    <Button size="sm" onClick={() => updateEngagement(selectedAssessmentId, 'consultation_booked')} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid="btn-consultation-booked">
                      Mark Booked
                    </Button>
                  </div>
                </div>
              </div>

              {/* Category Breakdown */}
              <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-cyan-400" /> Category Breakdown</h3>
                <div className="space-y-3">
                  {assessmentDetail.score_data?.category_scores && Object.entries(assessmentDetail.score_data.category_scores).map(([cat, data]) => {
                    const barColor = data.score >= 70 ? 'bg-emerald-500' : data.score >= 50 ? 'bg-amber-500' : 'bg-red-500';
                    return (
                      <div key={cat}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-slate-300">{cat}</span>
                          <span className={`font-bold ${data.score >= 70 ? 'text-emerald-400' : data.score >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{data.score}%</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full">
                          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${data.score}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recommended Service */}
              {assessmentDetail.service_recommendation && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-violet-500/30">
                  <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Target className="h-4 w-4 text-violet-400" /> Recommended Service</h3>
                  <p className="text-violet-300 font-medium">{assessmentDetail.service_recommendation.program_name}</p>
                  <p className="text-slate-400 text-sm mt-1">{assessmentDetail.service_recommendation.description}</p>
                </div>
              )}

              {/* Activity Log */}
              {assessmentDetail.engagement?.activities && assessmentDetail.engagement.activities.length > 0 && (
                <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-slate-400" /> Activity Timeline</h3>
                  <div className="space-y-2">
                    {assessmentDetail.engagement.activities.map((activity) => (
                      <div key={`${activity.timestamp}-${activity.action}`} className="flex items-center gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-cyan-500"></div>
                        <span className="text-slate-300">{activity.action.replace(/_/g, ' ')}</span>
                        <span className="text-slate-500 text-xs">{new Date(activity.timestamp).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
